function getProductFreeShipping(product) {
  return product?.shippingFree || false;
}

function isProduct$1(object) {
  return object?.apiAlias === "product";
}
function getMainImageUrl(object) {
  if (isProduct$1(object)) {
    return object?.cover?.media?.url || object?.media?.[0]?.media.url || "";
  }
  return object?.cover?.url || "";
}

function getTranslatedProperty(element, property) {
  return element?.translated?.[property] || element?.[property] || "";
}

function getProductName({ product } = {}) {
  if (!product) {
    return null;
  }
  return getTranslatedProperty(product, "name");
}

function getProductReviews({
  product
} = {}) {
  if (!product || !product.productReviews) {
    return [];
  }
  return product.productReviews.map(
    ({ id, externalUser, customerId, createdAt, content, points }) => ({
      id,
      author: externalUser ? externalUser : customerId,
      date: createdAt,
      message: content,
      rating: points
    })
  );
}

function getProductTierPrices(product) {
  if (!product || !product.calculatedPrices?.length) {
    return [];
  }
  const size = product.calculatedPrices.length;
  return product.calculatedPrices.map(({ unitPrice, quantity }, index) => ({
    label: index === size - 1 ? `from ${quantity}` : `to ${quantity}`,
    quantity,
    unitPrice
  }));
}

function getProductUrl(product) {
  if (!product)
    return "/";
  const seoUrl = product.seoUrls?.[0]?.seoPathInfo;
  return seoUrl ? `/${seoUrl}` : `/detail/${product.id}`;
}

function getProductRatingAverage(product) {
  return product?.ratingAverage;
}

function getProductThumbnailUrl(product) {
  const coverImageUrlFallback = product?.cover?.media?.url || "";
  const thumbnailImage = product?.cover?.media?.thumbnails?.length && product.cover.media.thumbnails.reduce(function(res, thumb) {
    return thumb.width < res.width ? thumb : res;
  }) || null;
  return thumbnailImage?.url || coverImageUrlFallback;
}

function getProductCalculatedListingPrice(product) {
  return product?.calculatedPrice?.listPrice?.price || product?.calculatedPrice?.unitPrice;
}

function getProductRealPrice(product) {
  if (!product) {
    return;
  }
  const real = product.calculatedPrice;
  if (product.calculatedPrices?.length > 1) {
    return product.calculatedPrices[product.calculatedPrices.length - 1];
  }
  return real;
}

function getProductFromPrice(product) {
  if (!product) {
    return;
  }
  const realPrice = getProductRealPrice(product);
  const displayFromPriceLabel = product.calculatedPrices?.length > 0;
  if (displayFromPriceLabel)
    return realPrice?.unitPrice;
}

function getProductRoute(product) {
  return {
    path: getProductUrl(product),
    state: {
      routeName: "frontend.detail.page",
      foreignKey: product?.id
    }
  };
}

function getEntityPrefix(category) {
  switch (category.linkType) {
    case "category":
      return "navigation";
    case "product":
      return "detail";
    case "landing_page":
      return "landingPage";
  }
}
function getCategoryUrl(category) {
  if (!category)
    return "/";
  switch (category.type) {
    case void 0:
      return "/";
    case "link":
      return category.externalLink || category?.seoUrls?.[0]?.seoPathInfo && `/${category?.seoUrls?.[0]?.seoPathInfo.replace(/^\/+/, "")}` || `/${getEntityPrefix(category)}/${category.internalLink}`;
    default:
      return category.seoUrls?.[0]?.seoPathInfo ? `/${category.seoUrls[0].seoPathInfo}` : `/${getEntityPrefix(category)}/${category.id}`;
  }
}

const isCategory$1 = (category) => {
  return category?.type === "page" || category?.type === "link" || category?.type === "folder";
};
function getCategoryImageUrl(category) {
  return isCategory$1(category) ? category?.media?.url || "" : "";
}

function getCategoryBreadcrumbs(category, options) {
  const breadcrumbs = category?.translated?.breadcrumb || category?.breadcrumb || [];
  const startIndex = options?.startIndex || 0;
  if (breadcrumbs.length <= startIndex)
    return [];
  return breadcrumbs.slice(startIndex).map((element) => {
    return {
      name: element
    };
  });
}

function getCategoryRoute(category) {
  if (!category)
    return "/";
  if (category.type === "page" || category.type === "link" && category.linkType === "category") {
    return {
      path: getCategoryUrl(category),
      state: {
        routeName: "frontend.navigation.page",
        foreignKey: category?.id
      }
    };
  }
  if (category.type === "link" && category.linkType === "product") {
    return {
      path: getCategoryUrl(category),
      state: {
        routeName: "frontend.detail.page",
        foreignKey: category?.internalLink
      }
    };
  }
  if (category.type === "link" && category.linkType === "landing_page") {
    return {
      path: getCategoryUrl(category),
      state: {
        routeName: "frontend.landing.page",
        foreignKey: category?.internalLink
      }
    };
  }
  return getCategoryUrl(category);
}

function isCmsSlot(content) {
  return content.apiAlias === "cms_slot";
}
function isCmsBlock(content) {
  return content.apiAlias === "cms_block";
}
function isCmsSection(content) {
  return content.apiAlias === "cms_section";
}
const deviceMap = {
  mobile: "md",
  tablet: "lg",
  desktop: "xl"
};
function getVisibilityClasses(content) {
  if (isCmsSlot(content) || !content?.visibility || Object.keys(content?.visibility).length === 0)
    return {};
  const visibilityCssClasses = {};
  Object.entries(content?.visibility)?.forEach(([device, isVisible]) => {
    if (!isVisible) {
      visibilityCssClasses[`${deviceMap[device]}:hidden`] = true;
    }
  });
  return visibilityCssClasses;
}
function getCmsLayoutConfiguration(content) {
  if (!content || isCmsSlot(content)) {
    return {
      cssClasses: null,
      layoutStyles: {}
    };
  }
  const visibilityCssClasses = getVisibilityClasses(content);
  const mappedCssClasses = typeof content.cssClass === "string" ? {
    ...content.cssClass.split(" ").reduce((accumulator, cssClass) => {
      return { ...accumulator, [cssClass]: true };
    }, {})
  } : {};
  const cssClasses = Object.keys(visibilityCssClasses).length ? Object.assign({}, mappedCssClasses, visibilityCssClasses) : mappedCssClasses;
  return {
    cssClasses,
    layoutStyles: {
      backgroundColor: content.backgroundColor,
      backgroundImage: content.backgroundMedia ? `url("${content.backgroundMedia.url}")` : null,
      backgroundSize: isCmsSection(content) ? content.backgroundMediaMode : null,
      sizingMode: isCmsSection(content) ? content.sizingMode : null,
      marginBottom: isCmsBlock(content) ? content.marginBottom : null,
      marginLeft: isCmsBlock(content) ? content.marginLeft : null,
      marginRight: isCmsBlock(content) ? content.marginRight : null,
      marginTop: isCmsBlock(content) ? content.marginTop : null
    }
  };
}

const DEFAULT_BG_IMAGE_SIZE = 800;
function roundUp(num) {
  return num ? Math.ceil(num / 100) * 100 : DEFAULT_BG_IMAGE_SIZE;
}
const getUrlFromBackgroundImage = (url) => {
  const regex = /(?:\(['"]?)(.*?)(?:['"]?\))/;
  const match = url.match(regex);
  return !match ? url : match[1];
};
function getBackgroundImageUrl(url, element) {
  const backgroundImage = getUrlFromBackgroundImage(url);
  const width = element.backgroundMedia?.metaData.width ?? 0;
  const height = element.backgroundMedia?.metaData.height ?? 0;
  const biggestParam = width > height ? `width=${roundUp(width > 1920 ? 1900 : width)}` : `height=${roundUp(height > 1920 ? 1900 : height)}`;
  const srcPath = `${backgroundImage}?${biggestParam}&fit=crop,smart`;
  return 'url("' + srcPath + '")';
}

function getCmsEntityObject(response) {
  switch (response.resourceType) {
    case "frontend.detail.page":
      return response.product;
    case "frontend.navigation.page":
      return response.category;
    case "frontend.landing.page":
      return response.landingPage;
  }
}
function isProduct(entity) {
  return entity.apiAlias === "product";
}
function isCategory(entity) {
  return entity.apiAlias === "category";
}
function isLandingPage(entity) {
  return entity.apiAlias === "landing_page";
}

function _parseUrlQuery(query) {
  const searchCriteria = {};
  if (!query || typeof query !== "object") {
    return searchCriteria;
  }
  Object.keys(query).forEach((key) => {
    try {
      searchCriteria[key] = typeof query[key] === "string" && ["{", "["].includes(query[key].charAt(0)) ? JSON.parse(query[key]) : query[key];
    } catch (e) {
      console.error(
        "[helpers][parseUrlQuery] Problem with resolving url param: " + key
      );
    }
  });
  return searchCriteria;
}

function _debounce(fn, delay = 300) {
  let prevTimer = null;
  return (...args) => {
    if (prevTimer) {
      clearTimeout(prevTimer);
    }
    prevTimer = window.setTimeout(() => {
      fn(...args);
      prevTimer = null;
    }, delay);
  };
}

const getFilter = (code, aggregation) => {
  return {
    label: getTranslatedProperty(aggregation, "name") || code,
    code,
    ...aggregation
  };
};
function isEntitiesAggregation(aggregation) {
  return aggregation.entities !== void 0;
}
function getListingFilters(aggregations) {
  if (!aggregations) {
    return [];
  }
  const transformedFilters = [];
  for (const [aggregationName, aggregation] of Object.entries(aggregations)) {
    if (aggregationName === "properties" && isEntitiesAggregation(aggregation)) {
      for (const filterEntity of aggregation.entities) {
        transformedFilters.push(getFilter(aggregationName, filterEntity));
      }
    } else if (!["properties", "options"].includes(aggregationName)) {
      transformedFilters.push(getFilter(aggregationName, aggregation));
    }
  }
  return transformedFilters;
}

const toFixedValue = 2;
const decimalsRegExp = new RegExp(`[.,]0{${toFixedValue}}$`);
function removeDecimals(value) {
  return value.replace(decimalsRegExp, "");
}
function getFormattedPrice(value, currency, options = {
  direction: "ltr",
  removeDecimals: false,
  removeCurrency: false
}) {
  let formattedPrice = [(+value).toFixed(toFixedValue), currency];
  if (options.removeDecimals) {
    formattedPrice[0] = removeDecimals(formattedPrice[0]);
  }
  if (options.removeCurrency) {
    formattedPrice.pop();
  }
  if (options.direction === "rtl" && !options.removeCurrency) {
    formattedPrice = formattedPrice.reverse();
  }
  return formattedPrice.join(" ");
}

function getSmallestThumbnailUrl(media) {
  if (!media || !media?.thumbnails?.length) {
    return;
  }
  const lowest = media.thumbnails.reduce((previous, current) => {
    return current.width < previous.width ? current : previous;
  });
  return lowest?.url;
}
function getBiggestThumbnailUrl(media) {
  if (!media?.thumbnails?.length) {
    return;
  }
  const biggest = media.thumbnails.reduce((previous, current) => {
    return current.width > previous.width ? current : previous;
  });
  return biggest?.url;
}
function getSrcSetForMedia(media) {
  if (!media?.thumbnails?.length) {
    return "";
  }
  return media.thumbnails.map((thumbnail) => {
    return `${thumbnail.url} ${thumbnail.width}w`;
  }).join(", ");
}

function getMedia(lineItem) {
  return lineItem.downloads?.reduce((acc, current) => {
    acc.push({
      id: current.id,
      fileName: `${current.media.fileName}.${current.media.fileExtension}`
    });
    return acc;
  }, []) || [];
}

function downloadFile(file, name) {
  const media = document.createElement("a");
  media.href = URL.createObjectURL(file);
  media.download = name;
  document.body.appendChild(media);
  media.click();
  document.body.removeChild(media);
}

function getShippingMethodDeliveryTime(shippingMethod) {
  return shippingMethod.deliveryTime?.translated?.name;
}

function getLanguageName(language) {
  return language.translationCode?.translated?.name || "";
}

function relativeUrlSlash(relativeUrl, slash = true) {
  const hasUrlSlash = relativeUrl.indexOf("/") === 0 ? true : false;
  if (slash) {
    return hasUrlSlash ? relativeUrl : `/${relativeUrl}`;
  }
  return hasUrlSlash ? relativeUrl.substring(1) : relativeUrl;
}

function urlIsAbsolute(url) {
  return new RegExp("^(?:[a-z+]+:)?//", "i").test(url);
}

export { _debounce, _parseUrlQuery, downloadFile, getBackgroundImageUrl, getBiggestThumbnailUrl, getCategoryBreadcrumbs, getCategoryImageUrl, getCategoryRoute, getCategoryUrl, getCmsEntityObject, getCmsLayoutConfiguration, getFormattedPrice, getLanguageName, getListingFilters, getMainImageUrl, getMedia, getProductCalculatedListingPrice, getProductFreeShipping, getProductFromPrice, getProductName, getProductRatingAverage, getProductReviews, getProductRoute, getProductThumbnailUrl, getProductTierPrices, getProductUrl, getShippingMethodDeliveryTime, getSmallestThumbnailUrl, getSrcSetForMedia, getTranslatedProperty, isCategory, isLandingPage, isProduct, relativeUrlSlash, urlIsAbsolute };
